import clsx from 'clsx'
import type { HTMLProps, MouseEventHandler, PropsWithChildren } from 'react'

export interface LinkProps
  extends PropsWithChildren,
    HTMLProps<HTMLAnchorElement> {
  /**
   * Optional link URL. If omitted, the link will function as a button (no redirect).
   */
  href?: string
  /**
   * Optional classname to append into the link.
   */
  className?: string
  /**
   * Optional. Whether to disable the link or not.
   */
  disabled?: boolean
  /**
   * Whether to inherit parent font and text styles
   */
  inheritFont?: boolean
  /**
   * Optional. Whether the link should be styled as an inline element.
   */
  inline?: boolean
  /**
   * Optional click handler.
   */
  onClick?: MouseEventHandler<HTMLAnchorElement>
  /**
   * Optional: Where to display the linked URL.
   */
  target?: React.HTMLAttributeAnchorTarget
  /**
   * Optional: Whether to underline the link text.
   */
  underline?: boolean
}

export default function Link({
  href,
  className = '',
  disabled = false,
  inheritFont = false,
  inline,
  underline = true,
  onClick,
  target = '_self',
  ...props
}: LinkProps) {
  const clickHandler: LinkProps['onClick'] = disabled
    ? (e) => {
        e.preventDefault()
      }
    : onClick

  return (
    <a
      className={clsx(
        'inline-flex items-center text-neutral-700 border border-transparent px-2 h-12 rounded-12',
        'focus:border-accent-link focus-visible:border-accent-link focus:outline-none focus-visible:outline-none',
        'aria-[disabled]:text-neutral-600 aria-[disabled]:cursor-not-allowed',
        underline
          ? 'underline underline-offset-[0.28em] hover:no-underline'
          : 'no-underline hover:underline underline-offset-[0.28em]',
        !inheritFont ? 'type-body-9 lg:type-body-5' : '',
        inline ? '!p-0 h-[unset]' : '',
        className,
      )}
      href={href || ''}
      role={!href ? 'button' : 'link'}
      onClick={clickHandler || (() => {})}
      target={target}
      {...(disabled ? { 'aria-disabled': true } : {})} // `disabled` prop is not a valid attribute for a link, so we use aria-disabled instead
      {...props}
    />
  )
}
